import { render } from 'solid-js/web'
import { Router, Route, Navigate, useParams } from '@solidjs/router'
import { QueryClient, QueryClientProvider } from '@tanstack/solid-query'
import { ErrorBoundary, lazy } from 'solid-js'
import { ToastRegion } from '@salun/ui'
import { installErrorReporter, reportBoundaryError } from './lib/errorReporter'
import './index.css'

installErrorReporter()

/** Redirect old top-level tenant page URLs to their new settings tab. */
function SettingsRedirect(props: { to: string }) {
  const params = useParams<{ slug: string }>()
  return <Navigate href={`/t/${params.slug}/settings/${props.to}`} />
}

const AdminLogin = lazy(() => import('./routes/admin/login'))
const AdminDashboard = lazy(() => import('./routes/admin/dashboard'))
const AdminTenants = lazy(() => import('./routes/admin/tenants'))
const AdminTenantDetail = lazy(() => import('./routes/admin/tenant-detail'))
const AdminTenantPayments = lazy(() => import('./routes/admin/tenant-payments'))
const AdminTenantProviderConfig = lazy(() => import('./routes/admin/tenant-provider-config'))
const AdminSystem = lazy(() => import('./routes/admin/system'))
const AdminDb = lazy(() => import('./routes/admin/db'))
const AdminLogs = lazy(() => import('./routes/admin/logs'))
const AdminMessagingStats = lazy(() => import('./routes/admin/messaging-stats'))
const AdminMessagingRateLimits = lazy(() => import('./routes/admin/messaging-rate-limits'))
const AdminSystemWa = lazy(() => import('./routes/admin/system-wa'))
const AdminSupport = lazy(() => import('./routes/admin/support'))
const AdminAntiAbuse = lazy(() => import('./routes/admin/anti-abuse'))
const AdminAnnouncements = lazy(() => import('./routes/admin/announcements'))
const AdminClientErrors = lazy(() => import('./routes/admin/client-errors'))
const AdminMarketing = lazy(() => import('./routes/admin/marketing'))
const AdminPayments = lazy(() => import('./routes/admin/payments'))
const AdminEmail = lazy(() => import('./routes/admin/email'))
const TenantLogin = lazy(() => import('./routes/tenant/login'))
const TenantDashboard = lazy(() => import('./routes/tenant/dashboard'))
const TenantOnboarding = lazy(() => import('./routes/tenant/onboarding'))
const TenantAgenda = lazy(() => import('./routes/tenant/agenda'))
const TenantCustomers = lazy(() => import('./routes/tenant/customers'))
const TenantStatistiche = lazy(() => import('./routes/tenant/statistiche'))
const PrivacyPage = lazy(() => import('./routes/legal/privacy'))
const TermsPage = lazy(() => import('./routes/legal/terms'))
const TenantCassa = lazy(() => import('./routes/tenant/cassa'))
const TenantMagazzino = lazy(() => import('./routes/tenant/magazzino'))
const TenantMagazzinoOrdini = lazy(() => import('./routes/tenant/magazzino-ordini'))
const TenantMagazzinoStatistiche = lazy(() => import('./routes/tenant/magazzino-statistiche'))
const TenantMagazzinoStocktake = lazy(() => import('./routes/tenant/magazzino-stocktake'))
const TenantGestione = lazy(() => import('./routes/tenant/gestione'))
const TenantAssistenza = lazy(() => import('./routes/tenant/assistenza'))
const NotFound = lazy(() => import('./routes/not-found'))

// Settings hub (consolidates account/servizi/staff/orari/risorse/prenotazioni/cassa/messaging/pagamenti)
const TenantSettingsLayout = lazy(() => import('./routes/tenant/settings/Layout'))
const TenantSettingsAccount = lazy(() => import('./routes/tenant/settings/account'))
const TenantSettingsServizi = lazy(() => import('./routes/tenant/settings/servizi'))
const TenantSettingsStaff = lazy(() => import('./routes/tenant/settings/staff'))
const TenantSettingsOrari = lazy(() => import('./routes/tenant/settings/orari'))
const TenantSettingsRisorse = lazy(() => import('./routes/tenant/settings/risorse'))
const TenantSettingsPrenotazioni = lazy(() => import('./routes/tenant/settings/prenotazioni'))
const TenantSettingsCassa = lazy(() => import('./routes/tenant/settings/cassa'))
const TenantSettingsMessaging = lazy(() => import('./routes/tenant/settings/messaging'))
const TenantSettingsPagamentiLayout = lazy(() => import('./routes/tenant/settings/PagamentiLayout'))
const TenantSettingsPagamentiConfig = lazy(() => import('./routes/tenant/settings/pagamentiConfig'))
const TenantSettingsPagamentiTransazioni = lazy(() => import('./routes/tenant/settings/pagamentiTransazioni'))
const TenantSettingsIntegrations = lazy(() => import('./routes/tenant/settings/integrations'))
const TenantSettingsTeam = lazy(() => import('./routes/tenant/settings/team'))
const TenantSettingsLoyalty = lazy(() => import('./routes/tenant/settings/loyalty'))
const TenantSettingsImportExport = lazy(() => import('./routes/tenant/settings/import-export'))
const ResetRequest = lazy(() => import('./routes/auth/reset-request'))
const ResetConfirm = lazy(() => import('./routes/auth/reset-confirm'))
const CustomerLogin = lazy(() => import('./routes/customer/login'))
const CustomerAuth = lazy(() => import('./routes/customer/auth'))
const CustomerTokenAuth = lazy(() => import('./routes/customer/token-auth'))
const CustomerBookings = lazy(() => import('./routes/customer/bookings'))
const BookingCancel = lazy(() => import('./routes/customer/booking-cancel'))
const PaySuccess = lazy(() => import('./routes/customer/pay-success'))
const PayCancel = lazy(() => import('./routes/customer/pay-cancel'))
const PublicBooking = lazy(() => import('./routes/public/booking'))
const PublicSignup = lazy(() => import('./routes/public/signup'))
const PublicLogin = lazy(() => import('./routes/public/login'))
const PublicBookingConfirm = lazy(() => import('./routes/public/booking-confirm'))
const BookingCancelPage = lazy(() => import('./routes/booking/cancel'))
const BookingConfirmPending = lazy(() => import('./routes/booking/confirm-pending'))

// Tenant-area route components — used to warm the chunk cache when the user
// lands on any /t/<slug>/* page so subsequent nav clicks don't show the
// fallback while a fresh chunk downloads.
const TENANT_ROUTES = [
  TenantDashboard,
  TenantAgenda,
  TenantCustomers,
  TenantStatistiche,
  TenantCassa,
  TenantMagazzino,
  TenantMagazzinoOrdini,
  TenantMagazzinoStatistiche,
  TenantMagazzinoStocktake,
  TenantGestione,
  TenantAssistenza,
  TenantSettingsLayout,
  TenantSettingsAccount,
  TenantSettingsServizi,
  TenantSettingsStaff,
  TenantSettingsOrari,
  TenantSettingsRisorse,
  TenantSettingsPrenotazioni,
  TenantSettingsCassa,
  TenantSettingsMessaging,
  TenantSettingsPagamentiLayout,
  TenantSettingsPagamentiConfig,
  TenantSettingsPagamentiTransazioni,
  TenantSettingsIntegrations,
  TenantSettingsTeam,
  TenantSettingsLoyalty,
] as const

function preloadTenantRoutes() {
  for (const r of TENANT_ROUTES) {
    // Solid's lazy() exposes `.preload()` to fetch the chunk eagerly.
    void (r as unknown as { preload?: () => Promise<unknown> }).preload?.()
  }
}

function maybePreloadForCurrentRoute() {
  if (/^\/t\/[^/]+/.test(window.location.pathname)) {
    const ric: typeof requestIdleCallback | undefined =
      (window as unknown as { requestIdleCallback?: typeof requestIdleCallback }).requestIdleCallback
    if (ric) ric(() => preloadTenantRoutes(), { timeout: 2000 })
    else setTimeout(preloadTenantRoutes, 500)
  }
}

// Disable refetchOnWindowFocus globally: on iOS Safari, mobile fast-scroll
// collapses the address bar and emits a focus/visibilitychange burst that
// triggered a wave of refetches, making the page appear to "re-render" mid-
// scroll. Mutations still invalidate their target queries explicitly.
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
})

function detectDensity(): 'comfortable' | 'compact' {
  const p = window.location.pathname
  if (p.startsWith('/admin') || /^\/t\/[^/]+/.test(p)) return 'compact'
  return 'comfortable'
}

function applyDensity() {
  document.documentElement.setAttribute('data-density', detectDensity())
}

applyDensity()
maybePreloadForCurrentRoute()
window.addEventListener('popstate', applyDensity)
window.addEventListener('popstate', maybePreloadForCurrentRoute)
// patch history.pushState to also fire
const _push = history.pushState
history.pushState = function (...args) {
  _push.apply(this, args as never)
  applyDensity()
  maybePreloadForCurrentRoute()
}

// When a new client build is deployed, hashed chunk filenames change and
// already-open tabs try to import files that no longer exist. Vite emits
// `vite:preloadError`; a plain full reload picks up the new index.html +
// current hashes.
let chunkReloadScheduled = false
window.addEventListener('vite:preloadError', () => {
  if (chunkReloadScheduled) return
  chunkReloadScheduled = true
  window.location.reload()
})
// Safety net for "Failed to fetch dynamically imported module" — the exact
// runtime form of vite:preloadError on Chromium. Match the FULL phrase only:
// matching bare "Failed to fetch" caught aborted XHRs/fetches (e.g. mobile
// fast-scroll cancelling in-flight requests) and triggered phantom reloads.
window.addEventListener('error', (e) => {
  const msg = (e.error as Error | undefined)?.message ?? ''
  if (/dynamically imported module/.test(msg) && !chunkReloadScheduled) {
    chunkReloadScheduled = true
    window.location.reload()
  }
})

render(
  () => (
    <ErrorBoundary
      fallback={(err) => {
        reportBoundaryError(err)
        return (
          <div class="min-h-screen flex items-center justify-center p-6 text-center">
            <div class="max-w-md">
              <h1 class="text-xl font-semibold mb-2">Si è verificato un errore</h1>
              <p class="text-sm text-muted mb-4">
                Ricarica la pagina per continuare. L'errore è stato segnalato.
              </p>
              <button
                type="button"
                class="rounded-md bg-accent text-white px-4 py-2 text-sm"
                onClick={() => window.location.reload()}
              >
                Ricarica
              </button>
            </div>
          </div>
        )
      }}
    >
    <QueryClientProvider client={qc}>
      <ToastRegion />
      <Router>
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin/dashboard" component={AdminDashboard} />
        <Route path="/admin/tenants" component={AdminTenants} />
        <Route path="/admin/tenants/:id" component={AdminTenantDetail} />
        <Route path="/admin/tenants/:id/payments" component={AdminTenantPayments} />
        <Route path="/admin/tenants/:id/provider-config" component={AdminTenantProviderConfig} />
        <Route path="/admin/system" component={AdminSystem} />
        <Route path="/admin/db" component={AdminDb} />
        <Route path="/admin/logs" component={AdminLogs} />
        <Route path="/admin/messaging" component={AdminMessagingStats} />
        <Route path="/admin/messaging/rate-limits" component={AdminMessagingRateLimits} />
        <Route path="/admin/anti-abuse" component={AdminAntiAbuse} />
        <Route path="/admin/announcements" component={AdminAnnouncements} />
        <Route path="/admin/errors" component={AdminClientErrors} />
        <Route path="/admin/marketing" component={AdminMarketing} />
        <Route path="/admin/payments" component={AdminPayments} />
        <Route path="/admin/email" component={AdminEmail} />
        <Route path="/admin/system-wa" component={AdminSystemWa} />
        <Route path="/admin/support" component={AdminSupport} />
        <Route path="/t/:slug/login" component={TenantLogin} />
        <Route path="/t/:slug/dashboard" component={TenantDashboard} />
        <Route path="/t/:slug/onboarding" component={TenantOnboarding} />
        <Route path="/t/:slug/agenda" component={TenantAgenda} />
        <Route path="/t/:slug/clienti" component={TenantCustomers} />
        <Route path="/t/:slug/statistiche" component={TenantStatistiche} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/termini" component={TermsPage} />
        <Route path="/t/:slug/cassa" component={TenantCassa} />
        <Route path="/t/:slug/magazzino" component={TenantMagazzino} />
        <Route path="/t/:slug/magazzino/ordini" component={TenantMagazzinoOrdini} />
        <Route path="/t/:slug/magazzino/statistiche" component={TenantMagazzinoStatistiche} />
        <Route path="/t/:slug/magazzino/stocktake" component={TenantMagazzinoStocktake} />
        <Route path="/t/:slug/gestione" component={TenantGestione} />
        <Route path="/t/:slug/assistenza" component={TenantAssistenza} />
        <Route path="/t/:slug/appointments" component={() => {
          const params = useParams<{ slug: string }>()
          return (
            <Navigate
              href={({ location }) => {
                const search = location.search ? `${location.search}&tab=lista` : '?tab=lista'
                return `/t/${params.slug}/agenda${search}`
              }}
            />
          )
        }} />

        {/* Settings hub — nested tabs */}
        <Route path="/t/:slug/settings" component={TenantSettingsLayout}>
          <Route path="/" component={() => <SettingsRedirect to="account" />} />
          <Route path="/account" component={TenantSettingsAccount} />
          <Route path="/servizi" component={TenantSettingsServizi} />
          <Route path="/staff" component={TenantSettingsStaff} />
          <Route path="/orari" component={TenantSettingsOrari} />
          <Route path="/risorse" component={TenantSettingsRisorse} />
          <Route path="/prenotazioni" component={TenantSettingsPrenotazioni} />
          <Route path="/cassa" component={TenantSettingsCassa} />
          <Route path="/messaging" component={TenantSettingsMessaging} />
          <Route path="/integrations" component={TenantSettingsIntegrations} />
          <Route path="/team" component={TenantSettingsTeam} />
          <Route path="/loyalty" component={TenantSettingsLoyalty} />
          <Route path="/import-export" component={TenantSettingsImportExport} />
          <Route path="/pagamenti" component={TenantSettingsPagamentiLayout}>
            <Route path="/" component={() => <SettingsRedirect to="pagamenti/configurazione" />} />
            <Route path="/configurazione" component={TenantSettingsPagamentiConfig} />
            <Route path="/transazioni" component={TenantSettingsPagamentiTransazioni} />
          </Route>
        </Route>

        {/* Legacy redirects → settings tabs */}
        <Route path="/t/:slug/account" component={() => <SettingsRedirect to="account" />} />
        <Route path="/t/:slug/services" component={() => <SettingsRedirect to="servizi" />} />
        <Route path="/t/:slug/staff" component={() => <SettingsRedirect to="staff" />} />
        <Route path="/t/:slug/hours" component={() => <SettingsRedirect to="orari" />} />
        <Route path="/t/:slug/resources" component={() => <SettingsRedirect to="risorse" />} />
        <Route path="/t/:slug/messaging" component={() => <SettingsRedirect to="messaging" />} />
        <Route path="/t/:slug/pagamenti" component={() => <SettingsRedirect to="pagamenti/configurazione" />} />
        <Route path="/t/:slug/pagamenti/transazioni" component={() => <SettingsRedirect to="pagamenti/transazioni" />} />
        <Route path="/reset" component={ResetRequest} />
        <Route path="/reset/:token" component={ResetConfirm} />
        <Route path="/c/:slug/login" component={CustomerLogin} />
        <Route path="/c/:slug/auth" component={CustomerAuth} />
        <Route path="/c/:slug/bookings" component={CustomerBookings} />
        <Route path="/c/:token/bookings/:bookingPublicId/cancel" component={BookingCancel} />
        <Route path="/c/pay/success" component={PaySuccess} />
        <Route path="/c/pay/cancel" component={PayCancel} />
        {/* Bare-token magic-link entry — must come AFTER more specific /c routes */}
        <Route path="/c/:token" component={CustomerTokenAuth} />
        <Route path="/b/:slug" component={PublicBooking} />
        <Route path="/signup" component={PublicSignup} />
        <Route path="/login" component={PublicLogin} />
        <Route path="/booking/confirm/:token" component={PublicBookingConfirm} />
        <Route path="/booking/cancel/:token" component={BookingCancelPage} />
        <Route path="/booking/confirm-pending/:token" component={BookingConfirmPending} />
        {/* Catch-all 404 — must be last */}
        <Route path="*" component={NotFound} />
      </Router>
    </QueryClientProvider>
    </ErrorBoundary>
  ),
  document.getElementById('root')!,
)
